import React, { useEffect, useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  Button,
  IconButton,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface Inventor {
  name: string;
  email: string;
  phone: string;
  organization: string;
  contributionPercentage: number;
  isPrimary: boolean;
}

interface Props {
  data: any;
  onChange: (data: any) => void;
  onComplete: (completed: boolean) => void;
}

const Inventors: React.FC<Props> = ({ data, onChange, onComplete }) => {
  const [inventors, setInventors] = useState<Inventor[]>(
    data.inventors || [
      {
        name: '',
        email: '',
        phone: '',
        organization: '',
        contributionPercentage: 100,
        isPrimary: true,
      },
    ]
  );

  const handleInventorChange = (index: number, field: string, value: any) => {
    const newInventors = [...inventors];
    newInventors[index] = { ...newInventors[index], [field]: value };

    // Adjust contribution percentages if needed
    if (field === 'contributionPercentage') {
      const total = newInventors.reduce((sum, inv) => sum + (Number(inv.contributionPercentage) || 0), 0);
      if (total > 100) {
        // Adjust other inventors proportionally
        const excess = total - 100;
        const othersTotal = total - Number(value);
        newInventors.forEach((inv, i) => {
          if (i !== index && othersTotal > 0) {
            const proportion = inv.contributionPercentage / othersTotal;
            inv.contributionPercentage = Math.max(0, inv.contributionPercentage - (excess * proportion));
          }
        });
      }
    }

    setInventors(newInventors);
    onChange({ ...data, inventors: newInventors });

    // Check completion
    const isComplete = newInventors.length > 0 && newInventors.every(
      inv => inv.name && inv.email && inv.contributionPercentage > 0
    );
    onComplete(isComplete);
  };

  const addInventor = () => {
    const newInventors = [
      ...inventors,
      {
        name: '',
        email: '',
        phone: '',
        organization: '',
        contributionPercentage: 0,
        isPrimary: false,
      },
    ];
    setInventors(newInventors);
    onChange({ ...data, inventors: newInventors });
  };

  const removeInventor = (index: number) => {
    if (inventors.length > 1) {
      const newInventors = inventors.filter((_, i) => i !== index);
      setInventors(newInventors);
      onChange({ ...data, inventors: newInventors });
    }
  };

  useEffect(() => {
    const isComplete = inventors.length > 0 && inventors.every(
      inv => inv.name && inv.email && inv.contributionPercentage > 0
    );
    onComplete(isComplete);
  }, []);

  const totalContribution = inventors.reduce((sum, inv) => sum + (Number(inv.contributionPercentage) || 0), 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Inventors
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          List all inventors and their contribution percentages. The total must equal 100%.
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Guidelines for Patent-Ready Description:
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Who is an Inventor?</strong> Only list people who contributed to the conception of the invention – the creative, intellectual contribution to the novel aspects. Those who only built, tested, or provided funding are not inventors. Incorrect inventorship can invalidate a patent.
        </Typography>

        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Contribution Percentages:</strong> Estimate each person's intellectual contribution to the inventive concepts. This doesn't need to be exact but should reflect relative contributions. These percentages may be used for royalty distribution or ownership discussions.
        </Typography>

        <Typography variant="body2">
          <strong>Accuracy is Critical:</strong> Ensure all contact information is current and accurate. Patent offices will use this information for official correspondence. Listing all true inventors is a legal requirement – intentionally omitting or falsely adding inventors can void patent rights.
        </Typography>
      </Alert>

      {totalContribution !== 100 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Total contribution: {totalContribution}%. Must equal 100%.
        </Alert>
      )}

      {inventors.map((inventor, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1">
              Inventor {index + 1} {inventor.isPrimary && '(Primary)'}
            </Typography>
            {inventors.length > 1 && (
              <IconButton onClick={() => removeInventor(index)} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                required
                label="Name"
                value={inventor.name}
                onChange={(e) => handleInventorChange(index, 'name', e.target.value)}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={inventor.email}
                onChange={(e) => handleInventorChange(index, 'email', e.target.value)}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Phone"
                value={inventor.phone}
                onChange={(e) => handleInventorChange(index, 'phone', e.target.value)}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                label="Organization"
                value={inventor.organization}
                onChange={(e) => handleInventorChange(index, 'organization', e.target.value)}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <TextField
                fullWidth
                required
                type="number"
                label="Contribution %"
                value={inventor.contributionPercentage}
                onChange={(e) => handleInventorChange(index, 'contributionPercentage', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, max: 100 }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inventor.isPrimary}
                    onChange={(e) => {
                      // Only one primary inventor
                      const newInventors = inventors.map((inv, i) => ({
                        ...inv,
                        isPrimary: i === index ? e.target.checked : false,
                      }));
                      setInventors(newInventors);
                      onChange({ ...data, inventors: newInventors });
                    }}
                  />
                }
                label="Primary Inventor"
              />
            </Box>
          </Box>
        </Paper>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addInventor}
        sx={{ mt: 2 }}
      >
        Add Another Inventor
      </Button>
    </Box>
  );
};

export default Inventors;